// src/components/ChatInterface.tsx
import React, { useState, useCallback, useEffect, useRef } from "react"; // Import useEffect, useRef
import { ReadyState } from "react-use-websocket";
import { ChatHeader } from "./chat-header";
import { MessageList } from "./message-list";
import { MessageInput } from "./message-input";
import type { DisplayMessage } from "../../types"; // Adjust path if needed
import { useUsername } from "../hooks/use-username"; // Import the new hook

interface ChatInterfaceProps {
  roomId: string;
  messages: DisplayMessage[];
  sendMessage: (username: string, message: string) => void;
  connectionStatus: string;
  chatError: string | null;
  readyState: ReadyState;
  isSidebar?: boolean;
  isYoutubeRoom?: boolean;
}

export function ChatInterface({
  roomId,
  messages,
  sendMessage: sendMessageFromHook,
  connectionStatus,
  chatError,
  readyState,
  isSidebar = false,
  isYoutubeRoom = false,
}: ChatInterfaceProps) {
  const [username, setUsername] = useUsername();
  const [usernameInput, setUsernameInput] = useState(username);
  const [isEditingUsername, setIsEditingUsername] = useState(false); // State for input visibility
  const usernameInputRef = useRef<HTMLInputElement>(null); // Ref for focusing

  // Keep local input synced with persisted username if it changes elsewhere
  useEffect(() => {
    setUsernameInput(username);
  }, [username]);

  // Focus the input when it becomes visible
  useEffect(() => {
    if (isEditingUsername) {
      usernameInputRef.current?.focus();
    }
  }, [isEditingUsername]);

  const handleUsernameInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setUsernameInput(e.target.value);
  };

  // Handler to save the username and hide the input
  const handleSetUsername = useCallback(() => {
    const trimmedUsername = usernameInput.trim();
    if (trimmedUsername && trimmedUsername !== username) {
      setUsername(trimmedUsername);
    } else {
      // If input is empty or unchanged, reset input value to current username
      setUsernameInput(username);
    }
    setIsEditingUsername(false); // Hide input after attempt
  }, [usernameInput, setUsername, username]);

  // Handle Enter key in username input
  const handleUsernameKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Enter") {
      handleSetUsername(); // Save and hide
      // e.currentTarget.blur(); // No longer needed as handleSetUsername hides it
    } else if (e.key === "Escape") {
      // Optional: Allow Escape key to cancel editing
      setUsernameInput(username); // Reset input value
      setIsEditingUsername(false); // Hide input
    }
  };

  // Toggle the visibility of the username input
  const toggleUsernameEdit = useCallback(() => {
    if (!isEditingUsername) {
      // If opening, ensure input value matches current username
      setUsernameInput(username);
    }
    setIsEditingUsername((prev) => !prev);
  }, [isEditingUsername, username]); // Add username dependency

  // Create the sendMessage function to pass down
  const handleSendMessage = useCallback(
    (message: string) => {
      sendMessageFromHook(username, message);
    },
    [sendMessageFromHook, username]
  );

  return (
    <div
      className={`flex h-full w-full flex-col bg-white ${
        isSidebar ? "p-3" : "p-4"
      }`}
    >
      {/* Pass username and toggle function to header */}
      <ChatHeader
        roomId={roomId}
        connectionStatus={connectionStatus}
        username={username}
        onEditUsername={toggleUsernameEdit} // Pass the toggle function
        isSidebar={isSidebar}
        isYoutubeRoom={isYoutubeRoom}
      />

      {/* Display combined errors */}
      {chatError && (
        <div className="mb-2 rounded bg-red-100 p-2 text-sm text-red-700">
          {chatError}
        </div>
      )}

      <MessageList messages={messages} />

      {/* --- Conditionally Rendered Username Input Area --- */}
      {isEditingUsername && (
        <div className="mb-3 flex items-center gap-2 border-t border-slate-200 pt-3">
          <label
            htmlFor="username-input-editable"
            className="flex-shrink-0 text-sm text-slate-600"
          >
            Set Username:
          </label>
          <input
            ref={usernameInputRef} // Assign ref
            id="username-input-editable"
            type="text"
            value={usernameInput}
            onChange={handleUsernameInputChange}
            onKeyPress={handleUsernameKeyPress}
            onBlur={handleSetUsername} // Save and hide on blur
            placeholder="Enter username"
            maxLength={30}
            className="flex-grow rounded-md border border-indigo-300 px-2 py-1 text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            autoFocus // Add autoFocus (might need ref for reliability)
          />
          {/* Optional: Add explicit Save/Cancel buttons here if needed */}
        </div>
      )}

      {/* Message Input remains the same */}
      <MessageInput
        sendMessage={handleSendMessage}
        readyState={readyState}
        isSidebar={isSidebar}
      />
    </div>
  );
}
